import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
// --- MODIFICA: Importa il nuovo hook useHomeTexts ---
import { useBranding, useDomain, useHomeTexts } from '../context/DomainContext';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Chip,
  Paper,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Dashboard as DashboardIcon,
  Rocket as RocketIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // --- HOOK ESISTENTI ---
  const { user: currentUser } = useAuth();
  const { config: domainData } = useDomain();
  const { 
    primaryColor, 
    logoUrl, 
    projectName, 
    brandName 
  } = useBranding();

  // --- NUOVO HOOK: Testi dinamici dal visual theme ---
  const homeTexts = useHomeTexts();

  // Funzione per ottenere le features del progetto con testi dinamici
  const getProjectFeatures = () => {
    const baseFeatures = [
      { 
        icon: <SecurityIcon sx={{ fontSize: 40, color: primaryColor }} />, 
        title: homeTexts.feature1Title, // Dinamico: desc_text2 o fallback
        description: "Autenticazione multi-provider e protezione dati enterprise" 
      },
      { 
        icon: <SpeedIcon sx={{ fontSize: 40, color: primaryColor }} />, 
        title: homeTexts.feature2Title, // Dinamico: desc_text3 o fallback
        description: "Architettura scalabile per prestazioni sempre al top" 
      },
      { 
        icon: <SupportIcon sx={{ fontSize: 40, color: primaryColor }} />, 
        title: homeTexts.feature3Title, // Dinamico: navbar_text1 o fallback
        description: "Team dedicato sempre disponibile per assistenza" 
      }
    ];

    // Feature speciale per MyBro (manteniamo la logica esistente)
    if (brandName?.toLowerCase().includes('mybro')) {
      baseFeatures.push({ 
        icon: <StarIcon sx={{ fontSize: 40, color: primaryColor }} />, 
        title: "Community MyBro", 
        description: "Connettiti con la community e condividi esperienze" 
      });
    }
    
    return baseFeatures;
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: 'grey.50' }}>
      {/* Hero Section */}
      <Box sx={{ background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}05 50%, transparent 100%)`, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                  {logoUrl && <Avatar src={logoUrl} sx={{ width: { xs: 60, md: 80 }, height: { xs: 60, md: 80 } }} />}
                  <Box>
                    {/* MODIFICA: Titolo dinamico dal visual theme */}
                    <Typography variant={isMobile ? "h4" : "h2"} sx={{ fontWeight: 800, color: primaryColor }}>
                      {brandName || projectName}
                    </Typography>
                    {brandName && projectName !== brandName && (
                      <Typography variant="h6" color="text.secondary" fontWeight={500}>{projectName}</Typography>
                    )}
                  </Box>
                </Stack>

                {/* MODIFICA: Sottotitolo dinamico con logica per utente loggato */}
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ color: 'text.primary', fontWeight: 500 }}>
                  {currentUser 
                    ? (homeTexts.welcomeBackMessage || `Bentornato, ${currentUser.displayName || 'Utente'}!`)
                    : homeTexts.heroSubtitle
                  }
                </Typography>

                {/* MODIFICA: Descrizione dinamica dal visual theme */}
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>
                  {currentUser 
                    ? "Accedi alla tua dashboard per gestire il tuo account." 
                    : homeTexts.heroDescription
                  }
                </Typography>

                {/* MODIFICA: Pulsanti con testi dinamici */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 2 }}>
                  {currentUser ? (
                    <Button 
                      variant="contained" 
                      size="large" 
                      onClick={() => navigate('/dashboard')} 
                      startIcon={<DashboardIcon />}
                    >
                      {homeTexts.dashboardButtonText}
                    </Button>
                  ) : (
                    <>
                      <Button 
                        variant="contained" 
                        size="large" 
                        onClick={() => navigate('/register')} 
                        startIcon={<RocketIcon />}
                      >
                        {homeTexts.buttonPrimaryText}
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="large" 
                        onClick={() => navigate('/login')} 
                        startIcon={<LoginIcon />}
                      >
                        {homeTexts.buttonSecondaryText}
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={8} sx={{ p: 4, borderRadius: 4, textAlign: 'center' }}>
                <RocketIcon sx={{ fontSize: { xs: 120, md: 160 }, color: primaryColor, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>Pronto al decollo?</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          {/* MODIFICA: Titolo features dinamico */}
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
            {homeTexts.featuresTitle}
          </Typography>
          {/* MODIFICA: Descrizione features dinamica */}
          <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem', maxWidth: 600, mx: 'auto' }}>
            {homeTexts.featuresDescription}
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {getProjectFeatures().map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card elevation={2} sx={{ height: '100%', borderRadius: 3 }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ mb: 3 }}>{feature.icon}</Box>
                  {/* MODIFICA: Titoli features ora dinamici */}
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{feature.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{feature.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      {!currentUser && (
        <Box sx={{ bgcolor: `${primaryColor}08`, py: 6 }}>
          <Container maxWidth="md">
            <Paper elevation={4} sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
              {/* MODIFICA: Titolo CTA dinamico */}
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                {homeTexts.ctaTitle}
              </Typography>
              {/* MODIFICA: Descrizione CTA dinamica */}
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {homeTexts.ctaDescription}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                {/* MODIFICA: Pulsanti CTA con testi dinamici */}
                <Button 
                  variant="contained" 
                  size="large" 
                  onClick={() => navigate('/register')} 
                  startIcon={<PersonAddIcon />}
                >
                  Registrati Gratis
                </Button>
                <Button 
                  variant="text" 
                  size="large" 
                  onClick={() => navigate('/login')} 
                  endIcon={<ArrowForwardIcon />}
                >
                  Hai già un account?
                </Button>
              </Stack>
            </Paper>
          </Container>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.100', borderTop: '1px solid', borderColor: 'grey.300', py: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} {brandName || projectName}. Tutti i diritti riservati.
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            Dominio: {domainData?.domain_name}
          </Typography>
          {/* DEBUG: Mostra informazioni sui testi caricati (rimuovi in produzione) */}
          {process.env.NODE_ENV === 'development' && (
            <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1, opacity: 0.7 }}>
              Debug: Testi da visual_theme - Progetto: {homeTexts.projectInfo.projectName} | Brand: {homeTexts.projectInfo.brandName}
            </Typography>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
